import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLElement} [viewMorePrevious] - The view more previous control.
 * @property {HTMLElement} [viewMoreNext] - The view more next control.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /** @type {boolean} */
  #isLoadingNext = false;

  /** @type {boolean} */
  #isLoadingPrevious = false;

  /** @type {number} */
  #currentPage = 1;

  /** @type {number} */
  #lowestLoadedPage = 1;

  /** @type {number} */
  #highestLoadedPage = 1;

  /** @type {boolean} */
  #isBackForwardNavigation = false;

  /** @type {boolean} */
  #canLoadPreviousPage = true;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#currentPage = this.#detectCurrentPage();
    this.#lowestLoadedPage = this.#currentPage;
    this.#highestLoadedPage = this.#currentPage;
    this.#isBackForwardNavigation = this.#currentPage > 1 || this.#detectBackForwardNavigation();
    this.#canLoadPreviousPage = true;
    this.#syncCurrentPageInUrl();
    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#attachPaginationListeners();
    this.#updatePaginationControls();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    window.addEventListener('pageshow', this.#handlePageShow);
    window.addEventListener('pagehide', this.#handlePageHide);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    this.#detachPaginationListeners();
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    window.removeEventListener('pageshow', this.#handlePageShow);
    window.removeEventListener('pagehide', this.#handlePageHide);
  }

  #attachPaginationListeners() {
    const { viewMorePrevious, viewMoreNext } = this.refs;
    viewMorePrevious?.addEventListener('click', this.#handlePreviousClick);
    viewMoreNext?.addEventListener('click', this.#handleNextClick);
  }

  /**
   * @returns {number}
   */
  #detectCurrentPage() {
    const url = new URL(window.location.href);
    const fromUrl = Number(url.searchParams.get('page'));
    if (!Number.isNaN(fromUrl) && fromUrl > 0) return fromUrl;

    const firstCard = this.refs.cards?.[0];
    const fromCard = Number(firstCard?.dataset.page);
    if (!Number.isNaN(fromCard) && fromCard > 0) return fromCard;

    return 1;
  }

  /**
   * @returns {number}
   */
  #getLastPage() {
    const { grid } = this.refs;
    return Number(grid?.dataset.lastPage ?? 1);
  }

  #detachPaginationListeners() {
    const { viewMorePrevious, viewMoreNext } = this.refs;
    viewMorePrevious?.removeEventListener('click', this.#handlePreviousClick);
    viewMoreNext?.removeEventListener('click', this.#handleNextClick);
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;
    if (this.#isLoadingNext) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    this.#setButtonLoadingState('next', true);
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    try {
      if (!nextPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolveNextPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('next');

        await promise;
        nextPageItemElements = this.#getGridForPage(nextPage.page);
        if (!nextPageItemElements) return;
      }

      grid.append(...nextPageItemElements);

      this.#aspectRatioHelper.processNewElements();

      this.#currentPage = nextPage.page;
      this.#highestLoadedPage = Math.max(this.#highestLoadedPage, nextPage.page);
      this.#updateHistory(nextPage.url);

      requestIdleCallback(() => {
        this.#fetchPage('next');
      });
    } finally {
      this.#setButtonLoadingState('next', false);
      this.#updatePaginationControls();
    }
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;
    if (this.#isLoadingPrevious) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;
    this.#setButtonLoadingState('previous', true);

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    try {
      if (!previousPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolvePreviousPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('previous');

        await promise;
        previousPageItemElements = this.#getGridForPage(previousPage.page);
        if (!previousPageItemElements) {
          this.#canLoadPreviousPage = false;
          return;
        }
      }

      // Store the current scroll position and height of the first element
      const scrollTop = window.scrollY;
      const firstElement = grid.firstElementChild;
      const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

      // Prepend the new elements
      grid.prepend(...previousPageItemElements);
      this.#canLoadPreviousPage = true;

      this.#aspectRatioHelper.processNewElements();
      this.#currentPage = previousPage.page;
      this.#lowestLoadedPage = Math.min(this.#lowestLoadedPage, previousPage.page);
      this.#updateHistory(previousPage.url);

      // Calculate and adjust scroll position to maintain the same view
      if (firstElement) {
        const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
        const heightDiff = newHeight - oldHeight;
        window.scrollTo({
          top: scrollTop + heightDiff,
          behavior: 'instant',
        });
      }

      requestIdleCallback(() => {
        this.#fetchPage('previous');
      });
    } finally {
      this.#setButtonLoadingState('previous', false);
      this.#updatePaginationControls();
    }
  }

  /**
   * Update the URL without creating extra history entries.
   * @param {URL} url
   */
  #updateHistory(url) {
    if (window.location.href === url.toString()) return;
    history.replaceState(history.state, '', url.toString());
  }

  #syncCurrentPageInUrl() {
    this.#updateHistory(this.#buildPageUrl(this.#currentPage));
  }

  /**
   * @returns {boolean}
   */
  #detectBackForwardNavigation() {
    const navigationEntry = /** @type {PerformanceNavigationTiming | undefined} */ (
      performance.getEntriesByType('navigation')[0]
    );
    const navigationType = typeof navigationEntry?.type === 'string' ? navigationEntry.type : null;

    if (navigationType === 'back_forward' || navigationType === 'reload') return true;

    // Legacy fallback for browsers without Navigation Timing Level 2 support.
    if (!performance.navigation) return false;

    return (
      performance.navigation.type === performance.navigation.TYPE_BACK_FORWARD
      || performance.navigation.type === performance.navigation.TYPE_RELOAD
    );
  }

  /**
   * @param {number} page
   * @returns {URL}
   */
  #buildPageUrl(page) {
    const url = new URL(window.location.href);
    if (page <= 1) {
      url.searchParams.delete('page');
    } else {
      url.searchParams.set('page', page.toString());
    }
    url.hash = '';
    return url;
  }

  /**
   * @param {'next' | 'previous'} type
   * @returns {HTMLButtonElement | null}
   */
  #getPaginationButton(type) {
    const control = type === 'next' ? this.refs.viewMoreNext : this.refs.viewMorePrevious;

    if (control instanceof HTMLButtonElement) {
      return control;
    }

    if (!(control instanceof HTMLElement)) {
      return null;
    }

    return control.querySelector('button');
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const isPrevious = type === 'previous';
    const page = isPrevious ? this.#lowestLoadedPage - 1 : this.#highestLoadedPage + 1;
    const url = this.#buildPageUrl(page);

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();
    this.#currentPage = this.#detectCurrentPage();
    this.#isBackForwardNavigation = this.#currentPage > 1 || this.#detectBackForwardNavigation();
    this.#lowestLoadedPage = this.#currentPage;
    this.#highestLoadedPage = this.#currentPage;
    this.#canLoadPreviousPage = true;

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#detachPaginationListeners();
        this.#attachPaginationListeners();
        this.#currentPage = this.#detectCurrentPage();
        this.#isBackForwardNavigation = this.#currentPage > 1 || this.#detectBackForwardNavigation();
        this.#lowestLoadedPage = this.#currentPage;
        this.#highestLoadedPage = this.#currentPage;
        this.#updatePaginationControls();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };

  /**
   * Re-initialize pagination after bfcache restore.
   * @param {PageTransitionEvent} event
   */
  #handlePageShow = (event) => {
    if (!event.persisted) return;

    this.#isBackForwardNavigation = true;
    this.#canLoadPreviousPage = true;
    this.pages.clear();
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();
    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    this.#detachPaginationListeners();
    this.#attachPaginationListeners();
    this.#currentPage = this.#detectCurrentPage();
    this.#isBackForwardNavigation = this.#currentPage > 1 || this.#detectBackForwardNavigation();
    this.#lowestLoadedPage = this.#currentPage;
    this.#highestLoadedPage = this.#currentPage;
    this.#updatePaginationControls();
    this.#fetchPage('next');
    this.#fetchPage('previous');
  };

  /**
   * Pause observers when page is going into bfcache.
   */
  #handlePageHide = () => {
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
      this.infinityScrollObserver = undefined;
    }
  };

  #updatePaginationControls() {
    const { viewMorePrevious, viewMoreNext } = this.refs;
    const lastPage = this.#getLastPage();
    const hasPrevious = this.#isBackForwardNavigation && this.#lowestLoadedPage > 1 && this.#canLoadPreviousPage;
    const hasNext = this.#highestLoadedPage < lastPage;

    if (viewMorePrevious instanceof HTMLElement) {
      viewMorePrevious.hidden = !hasPrevious;
      const previousButton = this.#getPaginationButton('previous');
      if (previousButton) {
        previousButton.disabled = !hasPrevious || this.#isLoadingPrevious;
      }
    }

    if (viewMoreNext instanceof HTMLElement) {
      viewMoreNext.hidden = !hasNext;
      const nextButton = this.#getPaginationButton('next');
      if (nextButton) {
        nextButton.disabled = !hasNext || this.#isLoadingNext;
      }
    }
  }

  /**
   * @param {'next' | 'previous'} type
   * @param {boolean} value
   */
  #setButtonLoadingState(type, value) {
    const button = this.#getPaginationButton(type);
    if (!button) return;

    if (type === 'next') {
      this.#isLoadingNext = value;
    } else {
      this.#isLoadingPrevious = value;
    }

    const defaultLabel = button.getAttribute('data-label') ?? button.textContent?.trim() ?? '';
    const loadingLabel = button.getAttribute('data-loading-label') ?? 'Loading...';

    button.setAttribute('data-label', defaultLabel);
    button.textContent = value ? loadingLabel : defaultLabel;
    button.setAttribute('aria-busy', String(value));
    button.disabled = value;
  }

  /**
   * @param {MouseEvent} event
   */
  #handleNextClick = async (event) => {
    event.preventDefault();
    if (viewTransition.current) await viewTransition.current;
    await this.#renderNextPage();
  };

  /**
   * @param {MouseEvent} event
   */
  #handlePreviousClick = async (event) => {
    event.preventDefault();
    if (viewTransition.current) await viewTransition.current;
    await this.#renderPreviousPage();
  };
}
